import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ShopLayout from './ShopLayout.js';
import { magazineApi } from '../../services/magazineApi.js';
import { useCart } from '../../contexts/CartContext.js';
import { useSeo } from '../Common/SeoHead.js';
import { isIssuePurchasable, issuePurchaseBlockedMessage } from '../../utils/issuePurchaseWindow.js';
import { toast } from 'react-toastify';

const FLARE_RED = '#c4151c';
const FLARE_RED_DARK = '#9f1016';

const Icon = ({ path, className = "h-5 w-5" }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} /></svg>;

export default function MagazineDetailSection() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const seo = useSeo();
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [deliveryType, setDeliveryType] = useState('Digital');
  const [quantity, setQuantity] = useState(1);
  const [busyAdd, setBusyAdd] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setNotFound(false);
        const r = await magazineApi.getPublishedBySlug(slug);
        if (cancelled) return;
        let formatted = null;
        if (r?.data?.data_formatted?.id) formatted = r.data.data_formatted;
        else if (r?.data?.data?.id) formatted = magazineApi.formatIssue(r.data.data);
        else if (r?.data?.magazine_issue?.id) formatted = magazineApi.formatIssue(r.data.magazine_issue);
        else if (r?.data?.id) formatted = magazineApi.formatIssue(r.data);
        if (!formatted || !formatted.id) { setNotFound(true); return; }
        setIssue(formatted);
        if (!formatted.is_physical_available) setDeliveryType('Digital');
        if (formatted.is_digital_free) setDeliveryType('Digital');
      } catch (e) {
        const status = e?.response?.status;
        if (status === 404) setNotFound(true);
        else toast.error(`Failed to load magazine: ${e.message || 'Unknown error'}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!seo?.setOverride) return undefined;
    if (!issue) {
      seo.setOverride(null);
      return undefined;
    }
    seo.setOverride({
      title: issue.seo_title || issue.title,
      description: issue.seo_description || undefined,
      keywords: issue.seo_keywords || undefined,
      index: true,
    });
    return () => seo.setOverride(null);
  }, [issue, seo?.setOverride]);

  if (loading) {
    return (
      <ShopLayout>
        <div className="max-w-6xl mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-10">
            <div className="md:col-span-2 aspect-[3/4] bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 rounded-3xl animate-pulse" />
            <div className="md:col-span-3 space-y-4 pt-4">
              <div className="h-4 bg-gray-200 rounded animate-pulse w-36" />
              <div className="h-10 bg-gray-200 rounded animate-pulse w-3/4" />
              <div className="h-5 bg-gray-100 rounded animate-pulse w-2/3" />
              <div className="h-32 bg-gray-100 rounded-2xl animate-pulse" />
              <div className="h-28 bg-gray-100 rounded-2xl animate-pulse" />
            </div>
          </div>
        </div>
      </ShopLayout>
    );
  }

  if (notFound || !issue) {
    return (
      <ShopLayout>
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-3xl flex items-center justify-center" style={{ backgroundColor: '#fff1f2' }}>
            <Icon path="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" className="h-10 w-10" style={{ color: FLARE_RED }} />
          </div>
          <h1 className="text-3xl font-black text-gray-900 mb-3">Issue not found</h1>
          <p className="text-gray-600 mb-8">We couldn't find this magazine issue. It may be unpublished or the link may be outdated.</p>
          <Link to="/magazines" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold shadow-sm" style={{ backgroundColor: FLARE_RED }}>
            Browse All Issues
            <Icon path="M14 5l7 7m0 0l-7 7m7-7H3" className="h-4 w-4" />
          </Link>
        </div>
      </ShopLayout>
    );
  }

  const unitPrice = (() => {
    if (deliveryType === 'Digital' && issue.is_digital_free) return 0;
    return deliveryType === 'Digital'
      ? Number(issue.digital_price_pkr) || 0
      : Number(issue.physical_price_pkr) || 0;
  })();

  const lineTotal = unitPrice * Math.max(1, quantity);

  const isFullFree = issue.is_digital_free && deliveryType === 'Digital';
  const canBuy = isIssuePurchasable(issue);

  const onAddToCart = async () => {
    if (!canBuy) {
      toast.info(issuePurchaseBlockedMessage());
      return;
    }
    setBusyAdd(true);
    try {
      const result = addItem(issue, { delivery_type: deliveryType, quantity });
      if (result && result.ok === false) {
        toast.info(result.message || issuePurchaseBlockedMessage());
        return;
      }
      toast.success(`Added ${quantity}× ${issue.title || issue.month_year || 'issue'} (${deliveryType}) to your cart.`);
      setTimeout(() => navigate('/cart'), 250);
    } finally {
      setTimeout(() => setBusyAdd(false), 500);
    }
  };

  const onBuyNow = async () => {
    if (!canBuy) {
      toast.info(issuePurchaseBlockedMessage());
      return;
    }
    setBusyAdd(true);
    try {
      const result = addItem(issue, { delivery_type: deliveryType, quantity });
      if (result && result.ok === false) {
        toast.info(result.message || issuePurchaseBlockedMessage());
        return;
      }
      setTimeout(() => navigate('/checkout'), 200);
    } finally {
      setTimeout(() => setBusyAdd(false), 500);
    }
  };

  return (
    <ShopLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 lg:pt-14 pb-28 md:pb-14">
        <div className="mb-6">
          <Link to="/magazines" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors">
            <Icon path="M10 19l-7-7m0 0l7-7m-7 7h18" className="h-4 w-4" />
            Back to all magazines
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-10 lg:gap-14 items-start">
          <div className="md:col-span-2 w-full min-w-0 md:sticky md:top-24">
            <div className="relative aspect-[3/4] w-full rounded-3xl overflow-hidden shadow-2xl border border-gray-100 bg-white">
              {issue.cover_image_url ? (
                <img src={issue.cover_image_url} alt={issue.title || issue.month_year || 'Flare cover'} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = '0.3'; }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-rose-100 via-white to-indigo-100">
                  <span className="text-3xl font-black" style={{ color: FLARE_RED }}>{issue.month_year}</span>
                </div>
              )}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold text-white shadow" style={{ backgroundColor: FLARE_RED }}>
                  <Icon path="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" className="h-3.5 w-3.5" />
                  Published
                </div>
                {issue.is_digital_free && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-extrabold text-white shadow-lg bg-emerald-500 animate-pulse">
                    <Icon path="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" className="h-3.5 w-3.5" />
                    FREE DIGITAL EDITION
                  </div>
                )}
              </div>
            </div>
            {issue.pdf_file_url && (
              <a href={issue.pdf_file_url} target="_blank" rel="noopener noreferrer" className="mt-6 w-full max-w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-bold bg-white border border-gray-200 text-gray-800 hover:bg-gray-50 shadow-sm text-center">
                <Icon path="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" className="h-5 w-5 shrink-0" />
                {issue.is_digital_free ? 'Read Online · Full PDF' : `Preview Full PDF · First ${issue.preview_pages_count || '12'} pages free`}
              </a>
            )}
          </div>

          <div className="md:col-span-3 space-y-6 min-w-0">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: FLARE_RED }}>
                <Icon path="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />
                {issue.month_year}
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-gray-900 mb-3 leading-[1.1] break-words">
                {issue.title || `Flare Magazine · ${issue.month_year}`}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-50 border border-gray-200 font-semibold">
                  {issue.volume ? `Volume ${issue.volume}` : ''}{issue.volume && issue.issue_no ? ' · ' : ''}{issue.issue_no ? `Issue ${issue.issue_no}` : ''}
                </span>
                {issue.editor && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-50 border border-rose-100 font-semibold" style={{ color: FLARE_RED_DARK }}>
                    <Icon path="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" className="h-3.5 w-3.5" />
                    Editor: {issue.editor}
                  </span>
                )}
                {issue.preview_pages_count && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-800 font-semibold">
                    <Icon path="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" className="h-3.5 w-3.5" />
                    {issue.preview_pages_count} preview pages
                  </span>
                )}
              </div>
            </div>

            <div className="rounded-3xl bg-white border border-gray-100 shadow-sm p-6 lg:p-7">
              <div className="mb-5">
                <h2 className="text-lg font-extrabold text-gray-900 mb-3">Choose your format</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label
                    className={`cursor-pointer rounded-2xl border-2 p-4 transition-all ${
                      deliveryType === 'Digital'
                        ? (issue.is_digital_free ? 'border-emerald-500 bg-emerald-50 shadow-sm' : 'border-emerald-500 bg-emerald-50 shadow-sm')
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <input type="radio" name="delivery_type" value="Digital" checked={deliveryType === 'Digital'} onChange={() => setDeliveryType('Digital')} className="sr-only" />
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-extrabold text-gray-900 flex items-center gap-2">
                          <Icon path="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" className={`h-5 w-5 ${issue.is_digital_free ? 'text-emerald-600' : 'text-emerald-600'}`} />
                          Digital Edition
                          {issue.is_digital_free && (
                            <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black text-white bg-emerald-500">
                              <Icon path="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" className="h-3 w-3" />
                              FREE
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Instant email · High-quality PDF · Any device</div>
                      </div>
                      <div className="text-right">
                        {issue.is_digital_free ? (
                          <div className="font-black text-xl text-emerald-700 flex items-center justify-end gap-1">
                            <Icon path="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" className="h-5 w-5" />
                            FREE
                          </div>
                        ) : (
                          <div className="font-black text-xl text-emerald-800">Rs {Number(issue.digital_price_pkr).toLocaleString('en-PK')}</div>
                        )}
                      </div>
                    </div>
                  </label>

                  <label
                    className={`cursor-pointer rounded-2xl border-2 p-4 transition-all ${
                      !issue.is_physical_available ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''
                    } ${
                      issue.is_physical_available && deliveryType === 'Physical'
                        ? 'shadow-sm'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                    style={issue.is_physical_available && deliveryType === 'Physical' ? { borderColor: FLARE_RED, backgroundColor: '#fff1f2' } : {}}
                  >
                    <input
                      type="radio"
                      name="delivery_type"
                      value="Physical"
                      checked={deliveryType === 'Physical'}
                      disabled={!issue.is_physical_available}
                      onChange={() => issue.is_physical_available && setDeliveryType('Physical')}
                      className="sr-only"
                    />
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-extrabold text-gray-900 flex items-center gap-2">
                          <Icon path="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" className="h-5 w-5" style={{ color: FLARE_RED }} />
                          Printed Edition
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Premium paper · Nationwide courier · 3-5 days</div>
                      </div>
                      <div className="text-right">
                        {issue.is_physical_available ? (
                          <div className="font-black text-xl" style={{ color: FLARE_RED_DARK }}>Rs {Number(issue.physical_price_pkr).toLocaleString('en-PK')}</div>
                        ) : (
                          <div className="text-sm font-bold text-gray-400 mt-1">Sold out</div>
                        )}
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {!isFullFree ? (
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 items-end">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-bold text-gray-800 mb-1.5">Quantity</label>
                    <div className="flex items-center border border-gray-200 rounded-xl bg-white overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setQuantity(q => Math.max(1, (Number(q) || 1) - 1))}
                        className="w-12 h-12 text-gray-600 hover:bg-gray-50 font-extrabold text-xl"
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min={1}
                        value={quantity}
                        onChange={e => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                        className="w-full h-12 text-center text-lg font-black bg-transparent focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                      <button
                        type="button"
                        onClick={() => setQuantity(q => Math.max(1, (Number(q) || 1) + 1))}
                        className="w-12 h-12 text-gray-600 hover:bg-gray-50 font-extrabold text-xl"
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div className="sm:col-span-3">
                    <div className="rounded-2xl p-4 bg-gradient-to-br from-gray-50 to-rose-50 border border-rose-100">
                      <div className="flex items-end justify-between gap-3">
                        <div>
                          <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Line total</div>
                          <div className="text-[11px] text-gray-500 mt-0.5">{quantity}× @ Rs {unitPrice.toLocaleString('en-PK')}</div>
                        </div>
                        <div className="text-3xl font-black tracking-tight" style={{ color: FLARE_RED }}>
                          Rs {lineTotal.toLocaleString('en-PK')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl p-5 bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-200">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-emerald-500 shrink-0 shadow">
                      <Icon path="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-black text-lg text-emerald-900 leading-tight">This digital edition is on us — enjoy it FREE!</h3>
                      <p className="text-sm text-emerald-700 mt-1 leading-relaxed">No payment, no checkout, no signup required. Click the button below to instantly read or download the complete PDF in your browser.</p>
                    </div>
                  </div>
                </div>
              )}

              {!canBuy ? (
                <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3.5 text-sm font-semibold text-amber-900">
                  {issuePurchaseBlockedMessage()} You can still browse this archive issue.
                </div>
              ) : null}

              {!isFullFree ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                  <button
                    onClick={onAddToCart}
                    disabled={!canBuy || busyAdd}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl font-bold border-2 transition-all disabled:opacity-60"
                    style={{
                      color: FLARE_RED,
                      borderColor: FLARE_RED,
                      backgroundColor: 'white',
                    }}
                  >
                    <Icon path="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" className="h-5 w-5" />
                    Add to Cart
                  </button>
                  <button
                    onClick={onBuyNow}
                    disabled={!canBuy || busyAdd}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl font-bold text-white shadow-lg transition-all disabled:opacity-60 hover:-translate-y-0.5"
                    style={{ backgroundColor: FLARE_RED, boxShadow: '0 10px 24px -10px rgba(196,21,28,0.5)' }}
                  >
                    Buy Now · Checkout
                    <Icon path="M14 5l7 7m0 0l-7 7m7-7H3" className="h-5 w-5" />
                  </button>
                </div>
              ) : (
                <div className="mt-6 space-y-3">
                  {issue.pdf_file_url && (
                    <a
                      href={issue.pdf_file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full max-w-full flex flex-wrap items-center justify-center gap-2 px-5 py-4 rounded-xl font-extrabold text-white shadow-lg transition-all hover:-translate-y-0.5 active:scale-[0.98] text-center"
                      style={{ backgroundColor: '#059669', boxShadow: '0 14px 32px -12px rgba(5,150,105,0.55)' }}
                    >
                      <Icon path="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" className="h-5 w-5" />
                      FREE INSTANT PDF DOWNLOAD
                      <span className="text-[11px] uppercase tracking-widest font-bold bg-white/20 px-2 py-0.5 rounded-md">· Full Issue</span>
                    </a>
                  )}
                  {issue.is_physical_available && canBuy && (
                    <button
                      onClick={() => setDeliveryType('Physical')}
                      className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-bold border-2 text-gray-700 border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all"
                    >
                      <Icon path="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" className="h-5 w-5" />
                      Also want the printed version? Switch to Physical Edition
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-3xl bg-white border border-gray-100 shadow-sm p-6 lg:p-7">
              <h2 className="text-lg font-extrabold text-gray-900 mb-3">About this issue</h2>
              {issue.description ? (
                <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {issue.description}
                </div>
              ) : (
                <div className="text-gray-500 leading-relaxed">
                  Packed with exclusive features, stunning photography, lifestyle trends, and Pakistan's most compelling stories — grab your copy of Flare Magazine now.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ShopLayout>
  );
}
